import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2, X, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_SEC = 20 * 60;

const SECTIONS = [
  ["queixa_principal", "QUEIXA PRINCIPAL"],
  ["hda", "HISTÓRIA DA DOENÇA ATUAL (S)"],
  ["objetivo", "EXAME FÍSICO / OBJETIVO (O)"],
  ["avaliacao", "AVALIAÇÃO / HIPÓTESES (A)"],
  ["conduta", "CONDUTA / PLANO (P)"],
  ["prescricao", "PRESCRIÇÃO"],
];

export const soapToText = (soap) =>
  SECTIONS.filter(([k]) => soap[k]).map(([k, label]) => `${label}:\n${soap[k]}`).join("\n\n");

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const pickMime = () => {
  const opts = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  return opts.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
};

const buildStream = async (extraStream, ctxRef) => {
  const mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
  if (!extraStream?.getAudioTracks?.().length) return { stream: mic, sources: [mic] };
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  ctxRef.current = ctx;
  const dest = ctx.createMediaStreamDestination();
  ctx.createMediaStreamSource(mic).connect(dest);
  ctx.createMediaStreamSource(new MediaStream(extraStream.getAudioTracks())).connect(dest);
  return { stream: dest.stream, sources: [mic] };
};

export const AmbientScribe = ({ onResult, extraStream, inline = false }) => {
  const [state, setState] = useState("idle");
  const [secs, setSecs] = useState(0);
  const [summary, setSummary] = useState("");
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const sourcesRef = useRef([]);
  const ctxRef = useRef(null);
  const timerRef = useRef(null);

  const cleanup = () => {
    clearInterval(timerRef.current);
    sourcesRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    sourcesRef.current = [];
    ctxRef.current?.close?.();
    ctxRef.current = null;
  };

  useEffect(() => () => { cleanup(); }, []);

  useEffect(() => {
    if (state === "recording" && secs === MAX_SEC - 120) toast.warning("Faltam 2 minutos para o limite de 20 minutos de gravação.");
    if (state === "recording" && secs >= MAX_SEC) { recRef.current?.stop(); }
  }, [secs, state]);

  const processAudio = async (blob) => {
    setState("processing");
    try {
      const fd = new FormData();
      fd.append("audio", blob, `consulta.${blob.type.includes("mp4") ? "mp4" : "webm"}`);
      const r = await fetch(`${API}/api/clinic/scribe`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${localStorage.getItem("veloclin_token")}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Erro ao processar áudio, tente novamente.");
      const text = soapToText(d.soap || {});
      if (!text) throw new Error("Nenhum conteúdo clínico identificado na gravação.");
      onResult(text);
      setSummary(d.soap.resumo_transcricao || "");
      toast.success("Prontuário preenchido pela IA", { description: "Revise e edite antes de salvar." });
    } catch (e) {
      toast.error(e.message === "Failed to fetch" ? "Erro ao processar áudio, tente novamente." : e.message);
    } finally {
      setState("idle");
    }
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error("Falha ao acessar microfone", { description: "Seu navegador não suporta gravação de áudio." });
      return;
    }
    try {
      const { stream, sources } = await buildStream(extraStream, ctxRef);
      sourcesRef.current = sources;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: 32000 });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        cleanup();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        chunksRef.current = [];
        processAudio(blob);
      };
      recRef.current = rec;
      rec.start(1000);
      setSecs(0);
      setSummary("");
      setState("recording");
      timerRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
      toast("Gravação iniciada", { description: "Limite de 20 minutos: concentre o essencial da consulta nesse período." });
    } catch {
      cleanup();
      toast.error("Falha ao acessar microfone", { description: "Permita o uso do microfone no navegador e tente novamente." });
    }
  };

  const stop = () => recRef.current?.state === "recording" && recRef.current.stop();

  const wrap = inline ? "flex flex-col items-end gap-2" : "fixed bottom-5 right-5 z-50 flex max-w-[calc(100vw-2.5rem)] flex-col items-end gap-2";

  return (
    <div className={wrap} data-testid="ambient-scribe">
      {summary && state === "idle" && (
        <div data-testid="scribe-summary" className="w-80 max-w-full rounded-2xl border border-blue-200 bg-white p-4 shadow-xl">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-blue-700"><Sparkles className="h-3.5 w-3.5" />Resumo da consulta</span>
            <button data-testid="scribe-summary-close" onClick={() => setSummary("")} className="rounded p-0.5 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
          </div>
          <p className="text-sm leading-relaxed text-slate-700">{summary}</p>
          <p className="mt-2 text-[11px] text-slate-400">Conteúdo gerado por IA. Revise os campos antes de salvar. O áudio não é armazenado.</p>
        </div>
      )}
      {state === "idle" && (
        <button data-testid="scribe-start-btn" onClick={start} className="group flex items-center gap-2 rounded-full bg-slate-900 py-3 pl-4 pr-5 text-sm font-bold text-white shadow-lg transition-transform duration-200 hover:-translate-y-0.5 hover:bg-blue-700">
          <Mic className="h-4 w-4" />
          <span className="flex flex-col items-start leading-tight">Gravar Consulta com IA<span data-testid="scribe-status" className="text-[10px] font-medium text-slate-300">Pronto para gravar</span></span>
        </button>
      )}
      {state === "recording" && (
        <button data-testid="scribe-stop-btn" onClick={stop} className="flex items-center gap-2.5 rounded-full bg-red-600 py-3 pl-4 pr-5 text-sm font-bold text-white shadow-lg ring-4 ring-red-200 hover:bg-red-700">
          <span className="relative flex h-3 w-3"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" /><span className="relative inline-flex h-3 w-3 rounded-full bg-white" /></span>
          <span className="flex flex-col items-start leading-tight"><span data-testid="scribe-status">Gravando áudio...</span><span data-testid="scribe-timer" className="font-mono text-[11px] font-medium text-red-100">{fmt(secs)} / 20:00</span></span>
          <Square className="ml-1 h-4 w-4 fill-white" />
        </button>
      )}
      {state === "processing" && (
        <div data-testid="scribe-processing" className="flex items-center gap-2 rounded-full bg-blue-600 py-3 pl-4 pr-5 text-sm font-bold text-white shadow-lg">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span data-testid="scribe-status">Processando inteligência...</span>
        </div>
      )}
    </div>
  );
};
